<script>
    import { onMount } from "svelte";

    import { user, isMobile } from "../../stores";

    import {
        SvelteTable,
        SearchInput,
        PageLoading,
        Button,
        NotificationModal,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getText,
        goBack,
        goToURL,
        isObjectEmpty,
        replaceURL,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { checkIfShowTab } from "../../Utils/utilRoute.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: columns = [
        {
            key: "group",
            title: "Group",
            headerClass:
                "text-left pl-4 text-[14px] min-w-[100px] rounded-tl-lg",
            class: "text-left pl-4 text-[14px] cursor-default",
            isHidden: !isUserAdmin,
            renderValue: (v) => getText(v.groupName),
        },
        {
            key: "surgeonName",
            title: "Surgeon Name",
            headerClass:
                "text-left pl-4 text-[14px] min-w-[100px] {isUserAdmin ? '' : 'rounded-tl-lg'}",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => getText(v.sugeonName),
        },
        {
            key: "contactName",
            title: "Contact Name",
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => getText(v.contactName),
        },
        {
            key: "preferredName",
            title: "Preferred Name",
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => getText(v.preferredName, "-"),
        },
        {
            key: "contactEmail",
            title: "Contact Email",
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => _getContactEmail(v.contactEmail),
            csvContent: (v) => _getContactEmail(v.contactEmail, true),
        },
        {
            key: "contactPhone",
            title: "Contact Phone",
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => getText(v.contactPhone),
        },
        {
            key: "location",
            title: "Location",
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: "text-left pl-4 text-[14px] cursor-default flex flex-col",
            renderValue: (v) => getText(v.location, "-"),
        },
        {
            key: "city",
            title: "City",
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => getText(v.city, "-"),
        },
        {
            key: "state",
            title: "State",
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-default",
            renderValue: (v) => getText(v.state, "-"),
        },
        {
            key: "postCode",
            title: "Post Code",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 w-[10%]",
            renderValue: (v) => getText(v.postCode, "-"),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3",
            renderValue: () => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    let isGettingSurgeryRooms = false,
        returnSurgeryRoomArr = [],
        filters = {},
        page = 0,
        pageSize = 25,
        count;

    let selectedSurgeryRoom = {},
        isDeleteSurgeryRoomModalOpen = false,
        isDeletingSurgeryRoom = false;

    onMount(() => {
        if (!checkIfShowTab($user.role, params.root)) {
            goBack();
            return;
        }

        if (params.query) updateSearchParamsByQuery(params.query);

        loadSurgeryRooms(page * pageSize, pageSize, _getSearchParams());
    });

    const reload = async () =>
        await loadSurgeryRooms(page * pageSize, pageSize, _getSearchParams());

    const handleDeleteSurgeryRoom = async () => {
        if (
            !selectedSurgeryRoom ||
            isObjectEmpty(selectedSurgeryRoom) ||
            !selectedSurgeryRoom.id
        )
            return;

        isDeletingSurgeryRoom = true;

        await apiMethods.deleteMethod
            .deleteSurgeryRoom(selectedSurgeryRoom.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to remove selected surgery room details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    _closeDeleteSurgeryRoomModal();
                    reload();
                }
            });

        isDeletingSurgeryRoom = false;
    };

    const createSurgeryRoom = () => goToURL("/SurgeryRooms/create/new");

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1];
            }
            updateURL();
        }
    };

    const loadSurgeryRooms = async (start, limit, params) => {
        isGettingSurgeryRooms = true;

        await apiMethods.getMethod
            .getSurgeryRooms(start, limit, params)
            .then((res) => _handleSurgeryRooms(res));

        isGettingSurgeryRooms = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadSurgeryRooms(0, pageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadSurgeryRooms(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadSurgeryRooms(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "contactEmail" && rowData.contactEmail)
            window.open(`mailto:${rowData.contactEmail}`);
        else if (key == "buttons") {
            if (className.includes("editIcon"))
                goToURL(`/SurgeryRooms/view/${rowData.id}`);
            else if (className.includes("deleteIcon"))
                _openDeleteSurgeryRoomModal(rowData);
        }
    };

    const updateURL = () =>
        replaceURL(
            `/SurgeryRooms/page=${page}&size=${pageSize}${_getParamsString()}`,
        );

    // HELPER FUNCTIONS
    const _handleSurgeryRooms = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnSurgeryRoomArr = res.results;
            count = res.total;
        }
    };

    const _getContactEmail = (email = "", isCsvContent = false) => {
        if (!email) return "-";

        return isCsvContent
            ? `${email}`
            : `<div class="cursor-pointer hover:underline">${email}</div>`;
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _renderActionIcons = () =>
        `
            <div class="flex flex-1 items-center justify-start gap-x-2">
                <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Surgery Room">
                    <i class="editIcon ${editIcon}"></i>
                </div>

                <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Remove Surgery Room">
                    <i class="deleteIcon ${deleteIcon}"></i>
                </div>
            </div>
        `;

    const _openDeleteSurgeryRoomModal = (selectedData = {}) => {
        isDeleteSurgeryRoomModalOpen = true;
        selectedSurgeryRoom = { ...selectedData };
    };

    const _closeDeleteSurgeryRoomModal = () => {
        isDeleteSurgeryRoomModalOpen = false;
        selectedSurgeryRoom = {};
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-col gap-y-4 flex-0">
        <div class="flex flex-row justify-between flex-wrap">
            <div class="flex gap-4 flex-wrap">
                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                    isLong
                />
            </div>

            <Button clickFunc={createSurgeryRoom}>Create Surgery Room</Button>
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingSurgeryRooms}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnSurgeryRoomArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnSurgeryRoomArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnSurgeryRoomArr,
            fileName: "Surgery Rooms",
            allDataApi: async () =>
                await apiMethods.getMethod.getSurgeryRooms(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<NotificationModal
    title="Remove Surgery Room"
    isModalOpen={isDeleteSurgeryRoomModalOpen}
    cancelText="Delete"
    submitText="Not now"
    confirmButtonClass="btn-accent"
    isReverseBtns
    closeModal={handleDeleteSurgeryRoom}
    confirmAction={_closeDeleteSurgeryRoomModal}
    isConfirmDisabled={isDeletingSurgeryRoom}
    closeModalFunction={_closeDeleteSurgeryRoomModal}
>
    Are you sure you want to remove this surgery room?
</NotificationModal>
